import { throttle, isMobile, wptemplate } from '../../../helper-functions'
import autocomplete from 'autocomplete.js'
import algoliasearch from 'algoliasearch'
import escapeHtml from 'escape-html'

/**
 * Init the search box autocomplete.
 */
export default function searchAutocomplete() {
  const algolia = window.algolia

  const mainContainer = document.querySelector( '.js-search-autocomplete' )

  if ( !mainContainer ) {
    return
  }

  const searchOverlay = mainContainer.querySelector( '.js-search-autocomplete-overlay' )
  const searchContainer = document.querySelector( '.search-form' )
  const searchInput = searchContainer.querySelector( '.search-field' )

  if ( !mainContainer || !algolia ) {
    return
  }

  if ( isMobile() ) {
    Array.from( document.querySelectorAll( '.js-mobile-search-button' ) ).forEach(
      searchButtonMobile => mobileSearchButton( searchButtonMobile )
    )
  }

  /**
   * Mobile Search Button that open the searc box in mobile.
   *
   * @param {object} searchButtonMobile The search button.
   */
  function mobileSearchButton( searchButtonMobile ) {
    searchButtonMobile.addEventListener( 'click', () => {
      searchContainer.classList.add( 'flex' )
      searchContainer.classList.remove( 'hidden' )
      searchInput.focus()
    })

    searchInput.addEventListener( 'blur', () => {
      searchContainer.classList.add( 'hidden' )
      searchContainer.classList.remove( 'flex' )
    })
  }

  /**
   * Creates the source for use in Autocomplete.js.
   *
   * @param {string} index The Algolia index name.
   * @param {array} params The index params.
   *
   * @returns {function} The function to do the search.
   */
  function newHitsSource( index, params ) {
    return function doSearch( query, cb ) {
      index
        .search( query, params )
        .then( res => cb( res.hits, res ) )
        .catch( () => cb([]) )
    }
  }

  /**
   * Init Algolia autocomplete.
   */
  function initAlgolia() {
    /* init Algolia client */
    const client = algoliasearch( algolia.application_id, algolia.search_api_key )

    /* setup default sources */
    const sources = []

    /* setup default result order */
    const sourcesOrder = [
      'page',
      'wp_posts_business-segment',
      'wp_posts_case-study',
      'wp_posts_product',
      'wp_posts_file',
    ]
    algolia.autocomplete.sources.sort( function sortSources( itemA, itemB ) {
      return sourcesOrder.indexOf( itemA.index_name ) - sourcesOrder.indexOf( itemB.index_name )
    })

    Array.from( algolia.autocomplete.sources ).forEach( source => {
      const suggestionTemplate = wptemplate( source.tmpl_suggestion )

      sources.push({
        source: newHitsSource( client.initIndex( source.index_name ), {
          hitsPerPage: source.max_suggestions,
          attributesToSnippet: [
            'content:10'
          ],
          highlightPreTag: '__ais-highlight__',
          highlightPostTag: '__/ais-highlight__',
          facetFilters: `taxonomies.language:${window.language.current_language}`
        }),
        templates: {
          header: ( query, data ) => wptemplate( 'autocomplete-header' )( data.hits[0]),
          suggestion: ( hit, data ) => {
            if ( hit.escaped === true ) {
              return suggestionTemplate( hit )
            }
            hit.escaped = true

            hit.query = data.query

            for ( const key in hit._highlightResult ) {
              /* We do not deal with arrays. */
              if ( typeof hit._highlightResult[ key ].value !== 'string' ) {
                continue
              }
              hit._highlightResult[ key ].value = escapeHtml( hit._highlightResult[ key ].value )
              hit._highlightResult[ key ].value = hit
                ._highlightResult[ key ].value
                .replace( /__ais-highlight__/g, '<em>' )
                .replace( /__\/ais-highlight__/g, '</em>' )
            }

            for ( const key in hit._snippetResult ) {
              /* We do not deal with arrays. */
              if ( typeof hit._snippetResult[ key ].value !== 'string' ) {
                continue
              }

              hit._snippetResult[ key ].value = escapeHtml( hit._snippetResult[ key ].value )
              hit._snippetResult[ key ].value = hit
                ._snippetResult[ key ].value
                .replace( /__ais-highlight__/g, '<em>' )
                .replace( /__\/ais-highlight__/g, '</em>' )
            }

            return suggestionTemplate( hit )
          },
          footer: ( hit, data ) => {
            return wptemplate( 'autocomplete-footer' )({
              'query': data.query
            })
          }
        }
      })
    })

    /**
     * Init the autocomplete widget.
     */
    function initAutocomplete() {
      let opened = false

      /* Setup dropdown menus */
      Array.from( document.querySelectorAll( '.search-field' ) ).forEach( searchInput => {
        const config = {
          debug: algolia.debug,
          hint: false,
          openOnFocus: true,
          appendTo: 'body',
          templates: {
            empty: wptemplate( 'autocomplete-empty' )
          }
        }

        /* Instantiate autocomplete.js */
        const autocompleteInstance = autocomplete( searchInput, config, sources )
          .on( 'autocomplete:selected', ( e, suggestion ) => {
            /* Redirect the user when we detect a suggestion selection. */
            window.location.href = suggestion.permalink
          })
          .on( 'autocomplete:opened', () => {
            opened = true
            searchOverlay.classList.remove( 'hidden' )
          })
          .on( 'autocomplete:updated', () => {
            addClassToLastNonEmpty(
              autocompleteInstance.autocomplete.getWrapper().querySelector( '.aa-dropdown-menu' ),
              'last-suggestion'
            )
          })
          .on( 'autocomplete:closed', function onAutocompleteClose() {
            opened = false
            searchOverlay.classList.add( 'hidden' )
            mainContainer.classList.remove( 'search-form-container--focus' )
            searchInput.blur()
          })

        if ( !isMobile() ) {
          /* Force the dropdown to be re-drawn on scroll to handle fixed containers. */
          window.addEventListener( 'scroll', throttle( () => {
            if ( opened ) {
              searchInput.blur()
            }
          }), 20 )
        }

        searchInput.addEventListener( 'focus', () => mainContainer.classList.add( 'search-form-container--focus' ) )

        searchInput.addEventListener( 'blur', event => {
          if ( event.relatedTarget && event.relatedTarget.type === 'submit' ) {
            if ( isMobile() ) {
              /* Fixes bug on mobile where the search button was not submitting */
              searchContainer.submit()
              autocompleteInstance.autocomplete.close()
            }
            return
          }

          mainContainer.classList.remove( 'search-form-container--focus' )
          autocompleteInstance.autocomplete.setVal( '' )
          autocompleteInstance.autocomplete.close()
        })
      })
    }

    initAutocomplete()
  }

  /**
   * Add a class to display the "see all results" button only on the last suggestion.
   *
   * @param {Element} container The main container element.
   * @param {Element} className The class to add.
   */
  function addClassToLastNonEmpty( container, className ) {
    const nonEmptyResults = Array.from( container.children )
      .map( item => {
        item.classList.remove( className )
        return item
      })
      .filter( item => item.innerHTML !== '' )

    if ( nonEmptyResults.length ) {
      nonEmptyResults
        .pop()
        .classList
        .add( className )
    }
  }

  initAlgolia()
}
